import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWindowScroll } from "react-use";

import type { ProductListGroupId, SelectedProduct } from "constants/modelCompareData/types";
import { CompareTable } from "./CompareTable/CompareTable";
import { SELECTED_PRODUCTS_LIMIT } from "./constants";
import * as S from "./ModelCompare.styles";
import type { ModelCompareProps } from "./ModelCompare.types";
import { ProductList } from "./ProductList/ProductList";

export const MODEL_COMPARE_V4_ANCHOR = "compare";

const INTERSECTION_OBSERVER_THRESHOLD = 0.35;

export const ModelCompare = ({
  headerStickOffset,
  overlayTopOffset,
  showBuyButton,
  onLinkClick,
  linkToStoreTitleClick,
  additionalProductData,
  section,
  defaultSelectedProductsCount = 2,
  selectedProducts: externalSelectedProducts,
  setSelectedProducts: setExternalSelectedProducts,
}: ModelCompareProps) => {
  const hasProductList = useMemo(
    () => section.products.length > 5 && !section.isStaticTable,
    [section.products, section.isStaticTable],
  );

  const intersectionObserverObjectRef = useRef<HTMLDivElement | null>(null);

  const { y: scrollPositionY } = useWindowScroll();

  const userIsUnderneathIntersectionObserver = useMemo(() => {
    if (!intersectionObserverObjectRef.current) return false;

    return (
      intersectionObserverObjectRef.current.getBoundingClientRect().bottom <
      scrollPositionY
    );
  }, [scrollPositionY]);

  /**
   * TODO: Improve the name and logic of this variable and related code.
   * Currently, this variable is used whether ModelCompare has a product list or not.
   * It should only be used if hasProductList is `true`. Why? Because if hasProductList
   * is `false` then selecting/deselecting products isn't possible. The gap between the
   * name of the variable and what it does can be confusing for future devs.
   */
  const defaultSelectedProducts = useMemo(
    () =>
      section.products
        .map((product) => ({ productId: product.id }))
        .slice(
          0,
          hasProductList
            ? defaultSelectedProductsCount
            : section.products.length,
        ),
    [defaultSelectedProductsCount, hasProductList, section.products],
  );
  /**
   * TODO: rewrite the "default selected products" feature so we don't need to use a `useEffect`
   * to update the state of V4. We'll be able to remove this hackie `useRef` as a side-effect.
   */
  const defaultSelectedProductsRef = useRef(defaultSelectedProducts);

  const selectedProductsLimit = useMemo(() => {
    return section.products.length < SELECTED_PRODUCTS_LIMIT
      ? section.products.length
      : SELECTED_PRODUCTS_LIMIT;
  }, [section.products.length]);

  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>(
    [],
  );

  const [hasBeenObserved, setHasBeenObserved] = useState(false);

  const showProductGroups = section.showProductGroups !== false;

  // #############################################################################
  // Sets selectedProducts value
  // #############################################################################

  useEffect(() => {
    defaultSelectedProductsRef.current = defaultSelectedProducts;
  }, [defaultSelectedProducts]);

  useEffect(() => {
    setSelectedProducts(
      externalSelectedProducts ||
        (defaultSelectedProductsRef.current as SelectedProduct[]),
    );
  }, [section, externalSelectedProducts]);

  // #############################################################################
  // Intersection Observer Logic
  // #############################################################################
  const observerCallback = useCallback<IntersectionObserverCallback>(
    (entries) => {
      entries.forEach(
        (entry) =>
          entry.isIntersecting && !hasBeenObserved && setHasBeenObserved(true),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setHasBeenObserved],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(observerCallback, {
      threshold: hasProductList ? INTERSECTION_OBSERVER_THRESHOLD : 0,
    });

    const intersectionObserverObjectRefCurrent =
      intersectionObserverObjectRef.current;

    if (intersectionObserverObjectRefCurrent)
      observer.observe(intersectionObserverObjectRefCurrent);

    return () => {
      if (intersectionObserverObjectRefCurrent) {
        observer.unobserve(intersectionObserverObjectRefCurrent);
        observer.disconnect();
      }
    };
  }, [observerCallback, hasProductList]);

  return (
    <S.Container>
      {hasProductList && (
        <ProductList
          isOverviewPage={Boolean(section.isOverviewPage)}
          initialActiveGroup={
            section.products[0]?.productListGroup?.id as ProductListGroupId
          }
          products={section.products}
          selectedProducts={selectedProducts}
          setSelectedProducts={setSelectedProducts}
          setExternalState={setExternalSelectedProducts}
          // TODO: rename this property now that `userIsUnderneathIntersectionObserver` has been added
          hasBeenObserved={
            hasBeenObserved || userIsUnderneathIntersectionObserver
          }
          selectedProductsLimit={selectedProductsLimit}
          showProductGroups={showProductGroups}
        />
      )}
      <CompareTable
        headerStickOffset={headerStickOffset}
        overlayTopOffset={overlayTopOffset}
        showBuyButton={showBuyButton}
        onLinkClick={onLinkClick}
        linkToStoreTitleClick={linkToStoreTitleClick}
        additionalProductData={additionalProductData}
        selectedProducts={selectedProducts}
        setSelectedProducts={setSelectedProducts}
        setExternalSelectedProducts={setExternalSelectedProducts}
        section={section}
        products={section.products}
        // TODO: rename this property now that `userIsUnderneathIntersectionObserver` has been added
        hasBeenObserved={
          hasBeenObserved || userIsUnderneathIntersectionObserver
        }
        selectedProductsLimit={selectedProductsLimit}
        isOverviewPage={Boolean(section.isOverviewPage)}
        isStaticTable={Boolean(section.isStaticTable)}
      />
      <S.InterSectionObserverObject
        ref={intersectionObserverObjectRef}
        // $hasProductList={hasProductList}
      />
    </S.Container>
  );
};
